import React from "react";
import {
  Autocomplete,
  Box,
  Breadcrumbs,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  Input,
  InputLabel,
  TextareaAutosize,
  TextField,
  Typography,
} from "@mui/material";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { ErrorMessage, Form, Formik } from "formik";
import { Link } from "react-router-dom";
import { TagsInput } from "react-tag-input-component";
import { useAddPartMutation } from "../../redux/reducer/features/Part/partSlice";
import LenghtUnitList from "../../compenents/app/LenghtUnitList";
import MassUnitList from "../../compenents/app/MassUnitList";
import PageLoader from "../../compenents/app/PageLoader";
import { useDispatch } from "react-redux";
import {
  Part,
  PartInitialValues,
  PartValidationSchema,
} from "../../models/features/Part/Parts";
import { useGetManufacturersQuery } from "../../redux/reducer/features/Product/productSlice";
import ManagePartImage from "./ManagePartImage";

export interface AddPartProps {}

export const AddPart: React.FunctionComponent<AddPartProps> = () => {
  const [partMutation, partMutationResult] = useAddPartMutation();
  const manufacturers = useGetManufacturersQuery();
  const [partData, setPartData] = React.useState<Part>();
  const [value, setValue] = React.useState(1);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const manufacturerDefaultProps = {
    options:
      manufacturers.data && manufacturers.data.entityList
        ? manufacturers.data.entityList
        : [],
    getOptionLabel: (option: any) => option.title,
  };

  const handleOnSubmit = (values: Part) => {
    partMutation({ data: values });
  };

  React.useEffect(() => {
    if (partMutationResult.isSuccess && partMutationResult.data.entity) {
      setValue(2);
      setPartData(partMutationResult.data.entity);
    }
  }, [partMutationResult]);

  return (
    <>
      <PageLoader isLoading={false} />
      <Container maxWidth={false}>
        <Card style={{ padding: "10px", margin: "10px" }}>
          <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/">
              Dashboard
            </Link>
            <Link color="inherit" to="/parts">
              Parts
            </Link>
            <Typography color="text.primary">Add Part</Typography>
          </Breadcrumbs>
        </Card>
        <Card style={{ padding: "10px", margin: "10px" }}>
          <TabContext value={value.toString()}>
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <TabList
                onChange={handleChange}
                aria-label="lab API tabs example"
              >
                <Tab label="Part Detail" value="1" />
                <Tab
                  label="Images"
                  value="2"
                  disabled={!partMutationResult.isSuccess}
                />
              </TabList>
            </Box>
            <TabPanel value="1">
              <Formik
                initialValues={partData ?? PartInitialValues}
                validationSchema={PartValidationSchema}
                onSubmit={(values) => {
                  handleOnSubmit(values);
                }}
              >
                {({
                  values,
                  errors,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                }) => (
                  <>
                    <Form>
                      <Grid container spacing={3} p={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            id="partNumber"
                            name="partNumber"
                            label="Part Number"
                            fullWidth
                            autoComplete="given-name"
                            variant="standard"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.partNumber}
                          />
                          <ErrorMessage
                            className="error-msg"
                            component={"div"}
                            name="partNumber"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <TextField
                            id="title"
                            name="title"
                            label="Title"
                            fullWidth
                            autoComplete="family-name"
                            variant="standard"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.title}
                          />
                          <ErrorMessage
                            className="error-msg"
                            component={"div"}
                            name="title"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            id="shortName"
                            name="shortName"
                            label="Short Name"
                            fullWidth
                            autoComplete="family-name"
                            variant="standard"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.shortName}
                          />
                          <ErrorMessage
                            className="error-msg"
                            component={"div"}
                            name="shortName"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Autocomplete
                            {...manufacturerDefaultProps}
                            id="clear-on-escape"
                            clearOnEscape
                            defaultValue={partData?.manufacturer}
                            onChange={(e, value) => {
                              console.log(values);
                              setFieldValue(
                                "manufacturerId",
                                value ? value.id : ""
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Manufacturer"
                                variant="standard"
                              />
                            )}
                          />
                          <ErrorMessage
                            className="error-msg"
                            component={"div"}
                            name="manufacturerId"
                          />
                        </Grid>

                        <Grid item xs={12} sm={3}>
                          <InputLabel htmlFor="dimension.depth" shrink>
                            Depth
                          </InputLabel>
                          <Input
                            id="dimension.depth"
                            name="dimension.depth"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dimension?.depth}
                            endAdornment={
                              <LenghtUnitList
                                value={values.dimension?.depthUnit}
                                id={"dimension.depthUnit"}
                                name={"dimension.depthUnit"}
                                onChange={handleChange}
                              ></LenghtUnitList>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <InputLabel htmlFor="dimension.height" shrink>
                            Height
                          </InputLabel>
                          <Input
                            id="dimension.height"
                            name="dimension.height"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dimension?.height}
                            endAdornment={
                              <LenghtUnitList
                                value={values.dimension?.heightUnit}
                                id={"dimension.heightUnit"}
                                name={"dimension.heightUnit"}
                                onChange={handleChange}
                              ></LenghtUnitList>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <InputLabel htmlFor="dimension.weight" shrink>
                            Weight
                          </InputLabel>
                          <Input
                            id="dimension.weight"
                            name="dimension.weight"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dimension?.weight}
                            endAdornment={
                              <MassUnitList
                                value={values.dimension?.weightUnit}
                                id={"dimension.weightUnit"}
                                name={"dimension.weightUnit"}
                                onChange={handleChange}
                              ></MassUnitList>
                            }
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <InputLabel htmlFor="dimension.width" shrink>
                            width
                          </InputLabel>
                          <Input
                            id="dimension.width"
                            name="dimension.width"
                            fullWidth
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dimension?.width}
                            endAdornment={
                              <LenghtUnitList
                                value={values.dimension?.widthUnit}
                                id={"dimension.widthUnit"}
                                name={"dimension.widthUnit"}
                                onChange={handleChange}
                              ></LenghtUnitList>
                            }
                          />
                        </Grid>

                        <Grid item xs={12} sm={12}>
                          <TagsInput
                            value={values.tags}
                            onChange={(tags) => {
                              setFieldValue("tags", tags);
                            }}
                            name="Tags"
                            placeHolder="enter tags"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextareaAutosize
                            aria-label="empty textarea"
                            placeholder="Description"
                            id={"description"}
                            name={"description"}
                            minRows={5}
                            style={{ width: "100%" }}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.description}
                          />
                          <ErrorMessage
                            className="error-msg"
                            component={"div"}
                            name="description"
                          />
                        </Grid>

                        <Grid mt={-2} item xs={12}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="secondary"
                                name="isEnabled"
                                value="yes"
                                checked={partData?.isEnabled}
                                onChange={(e) => {
                                  console.log(e);
                                  setFieldValue("isEnabled", e.target.checked);
                                }}
                              />
                            }
                            label="Enabled this part to be Use"
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Button
                            variant="contained"
                            type="submit"
                            onClick={() => {
                              console.log(errors);
                              console.log(values);
                            }}
                            endIcon={
                              partMutationResult.isLoading ? (
                                <CircularProgress color="inherit" size={30} />
                              ) : (
                                <></>
                              )
                            }
                            disabled={
                              partMutationResult.isLoading ||
                              partMutationResult.isSuccess
                            }
                          >
                            Save & Continue
                          </Button>
                        </Grid>
                      </Grid>
                    </Form>
                  </>
                )}
              </Formik>
            </TabPanel>
            <TabPanel value="2">
              <ManagePartImage
                partId={
                  partMutationResult.data?.entity?.id ??
                  "63fa7119-78cd-49f7-c74e-08dab59103b5"
                }
                images={partData?.images ?? []}
                partDetail={partData}
                setPartDetail={setPartData}
              ></ManagePartImage>
            </TabPanel>
          </TabContext>
        </Card>
      </Container>
    </>
  );
};
